import React, { createRef } from "react";
import { AxiosError } from "axios";
import cx from "classnames";
import { Button, IOption, getErrorPopupTexts } from "component-library";
import _debounce from "lodash/debounce";
import { connect } from "react-redux";
import { compose } from "redux";
import { IBreakdown } from "shared/models/breakdown";
import { IFacetGroup } from "shared/models/facet";
import { MapDispatchToProps } from "shared/types/utilities";
import {
  DEFAULT_CLIENT_AGENCY_ID,
  ErrorCode,
  NO_CLIENT_AGENCY_ID,
} from "config/common";
import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { FacetStoreKey } from "store/facet/facet.types";
import { hidePopup, showPopup } from "store/ui/ui.actions";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";
import { ISuggestion } from "models/suggestion";
import suggestionsService from "services/api/suggestions/suggestionsService";
import {
  DeleteSuggestions,
  GetSuggestions,
} from "services/api/suggestions/suggestionsService.types";
import {
  IBreakdownViewOnDataLoaded,
  IBreakdownViewRegisterRole,
  IBreakdownViewRole,
} from "components/BreakdownView/BreakdownView";
import MakeSuggestionsViewWrapper from "components/MakeSuggestionsView/MakeSuggestionsViewWrapper";
import {
  ISuggestionsViewTexts,
  prepareTexts,
} from "./SuggestionsViewAgent.helpers";
import getErrorPopupType from "helpers/getErrorPopupType";
import { Card } from "./Card/Card";
import "./SuggestionsViewAgent.scss";
import { dataTestIds } from "data-testids";

export interface ISuggestionsViewAgentLoadedData {
  agencyList?: ISuggestion[];
  clientsList?: ISuggestion[];
  removedList?: string[];
}

export interface ISuggestionsViewAgentActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface ISuggestionsViewAgentStoreProps {
  texts: ISuggestionsViewTexts;
  productionTypes: IFacetGroup[];
  getSuggestions: GetSuggestions;
  deleteSuggestions: DeleteSuggestions;
}

export interface ISuggestionsViewAgentProps
  extends ISuggestionsViewAgentStoreProps, ISuggestionsViewAgentActions {
  role: IBreakdownViewRole;
  location: IOption<string>[];
  breakdown: IBreakdown;
  isRoleClosed: boolean;
  switchEditMode: (isEdit: boolean) => void;
  isEditMode: boolean;
  disableEdit?: boolean;
  isPrintView?: boolean;
  reportConcernButton: React.ReactNode;
  registerRole?: (data: IBreakdownViewRegisterRole) => void;
  onDataLoaded?: (data: IBreakdownViewOnDataLoaded) => void;
  isRoleCast?: boolean | null;
}

export interface ISuggestionsViewAgentState {
  isLoading: boolean;
  agencyList: ISuggestion[];
  clientsList: ISuggestion[];
  removedList: string[];
}

export class SuggestionsViewAgent extends React.Component<
  ISuggestionsViewAgentProps,
  ISuggestionsViewAgentState
> {
  public requestCancellation = new AbortController();
  private suggestionRef = createRef<HTMLElement>();

  public readonly state: Readonly<ISuggestionsViewAgentState> = {
    isLoading: false,
    agencyList: [],
    removedList: [],
    clientsList: [],
  };

  private isInViewport(element: HTMLElement | null) {
    if (!element) return;

    const rect = element.getBoundingClientRect();

    return (
      rect.top !== 0 &&
      rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  private checkSuggestionIsVisible = _debounce(() => {
    const isView = this.isInViewport(this.suggestionRef.current);

    if (isView) {
      this.getSuggestions();

      document.removeEventListener(
        "scroll",
        this.checkSuggestionIsVisible,
        true
      );
    }
  }, 50);

  public componentDidMount() {
    const isView = this.isInViewport(this.suggestionRef.current);

    if (isView) {
      this.getSuggestions();

      return;
    }

    document.addEventListener("scroll", this.checkSuggestionIsVisible, true);
  }

  public componentDidUpdate(prevProps: ISuggestionsViewAgentProps) {
    if (
      prevProps.isEditMode !== this.props.isEditMode &&
      !this.props.isEditMode
    ) {
      void this.getSuggestions();
    }
  }

  public componentWillUnmount() {
    this.requestCancellation.abort?.();

    document.removeEventListener("scroll", this.checkSuggestionIsVisible, true);
  }

  public render() {
    const {
      isEditMode,
      disableEdit,
      role,
      breakdown,
      isPrintView,
      location,
      isRoleCast,
    } = this.props;
    const { isLoading } = this.state;

    if (isEditMode) {
      return (
        <MakeSuggestionsViewWrapper
          texts={this.props.texts}
          onToggleView={this.switchToEditMode(false)}
          hidePopup={this.props.hidePopup}
          showPopup={this.props.showPopup}
          handleError={this.handleError}
          roleId={role.id}
          genderIds={role.genderIds}
          breakdownId={breakdown.id}
          appearanceIds={role.appearanceIds}
          location={location}
        />
      );
    }

    const propsForPrimaryButton = this.getPropsForPrimaryButton(isRoleCast);

    return (
      <section
        className={cx("c-view-suggestions", {
          "c-view-suggestions--print": isPrintView,
        })}
        ref={this.suggestionRef}
      >
        {this.renderResultsForAgent()}
        <section className="c-view-suggestions__buttons-set g-col-lg-12 g-inner-grid">
          <div className="g-col-lg-9 g-col-md-8 c-view-suggestions__report-concerns">
            {this.props.reportConcernButton}
          </div>
          <div className="g-col-lg-3 g-col-md-4">
            <Button
              disabled={disableEdit}
              type={"primary"}
              loading={isLoading}
              data-testid={
                dataTestIds.nuJobsApp[
                  "SuggestionViewAgent.SuggestClient.Button"
                ]
              }
              {...propsForPrimaryButton}
            />
          </div>
        </section>
      </section>
    );
  }

  public renderResultsForAgent() {
    const { texts, breakdown, productionTypes, isRoleClosed, isPrintView } =
      this.props;
    const { agencyList, clientsList, isLoading, removedList } = this.state;
    const agencyListLength = agencyList.length;
    const clientsListLength = clientsList.length;

    if (isLoading) {
      return (
        <p className="c-view-suggestions__no-results g-bg-default">
          loading...
        </p>
      );
    }

    if (agencyListLength - removedList.length + clientsListLength === 0) {
      return (
        <p className="c-view-suggestions__no-results g-bg-default">
          {texts.noSuggestions}
        </p>
      );
    }

    return (
      <>
        {clientsListLength !== 0 &&
          this.renderSuggestions(
            texts.titleClient,
            clientsList,
            texts,
            breakdown,
            productionTypes,
            undefined,
            isPrintView
          )}
        {agencyListLength !== 0 &&
          this.renderSuggestions(
            texts.titleAgency,
            agencyList,
            texts,
            breakdown,
            productionTypes,
            isRoleClosed ? undefined : this.handleDelete,
            isPrintView
          )}
      </>
    );
  }

  public renderSuggestions(
    title: string,
    suggestions: ISuggestion[],
    texts: ISuggestionsViewTexts,
    breakdown: IBreakdown,
    productionTypes: IFacetGroup[],
    onDelete?: (ids: string[]) => Promise<boolean>,
    isPrintView?: boolean
  ) {
    return (
      <div className="c-view-suggestions__results g-bg-default">
        <h3 className="c-view-suggestions__title">{title}</h3>
        {suggestions.map((item) => (
          <Card
            suggestion={item}
            texts={texts}
            key={item.id}
            breakdown={breakdown}
            productionTypes={productionTypes}
            onDelete={onDelete}
            isPrintView={isPrintView}
          />
        ))}
      </div>
    );
  }

  public getPropsForPrimaryButton(isRoleCast?: boolean | null) {
    const { texts, isRoleClosed } = this.props;
    const { agencyList, removedList, isLoading } = this.state;

    if (isRoleClosed) {
      return { text: texts.roleClosed, disabled: true };
    } else if (isLoading) {
      return { text: "" };
    } else if (isRoleCast) {
      return { text: texts.isRoleCastButtonText, disabled: true };
    } else if (agencyList.length && removedList.length < agencyList.length) {
      return {
        text: texts.editSuggestion,
        onClick: this.switchToEditMode(true),
      };
    } else {
      return {
        text: texts.suggestClients,
        onClick: this.switchToEditMode(true),
      };
    }
  }

  private async getSuggestions() {
    const { role, registerRole } = this.props;

    if (registerRole) {
      const args: IBreakdownViewRegisterRole = {
        id: role.id,
        callback: (data: ISuggestionsViewAgentLoadedData) => {
          const filter = (list: ISuggestion[]) =>
            list.filter(
              (item: ISuggestion) => !data.removedList?.includes(item.id)
            );
          const clientsList = filter(
            data.clientsList ?? this.state.clientsList
          );
          const agencyList = filter(data.agencyList ?? this.state.agencyList);

          this.setState({
            clientsList,
            agencyList,
            removedList: data.removedList ?? this.state.removedList,
          });
        },
      };

      registerRole(args);

      return;
    }

    this.setState({ isLoading: true });
    this.requestCancellation.abort?.();
    this.requestCancellation = new AbortController();
    try {
      const suggestions = await this.props.getSuggestions(
        role.id,
        this.requestCancellation
      );
      this.splitSuggestions(suggestions, role.id);
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  }

  readonly handleError = (e: AxiosError) => {
    const texts = getErrorPopupTexts()[getErrorPopupType()];
    const error = e.response;
    // @ts-expect-error: data property may not exist on error.response
    const userFriendlyMessage = error?.data?.userFriendlyMessage;

    if (error && error.status === ErrorCode.BadRequest && userFriendlyMessage) {
      // @ts-expect-error: data property may not exist on error.response
      texts.description = `<p>${error.data.userFriendlyMessage}</p>`;
    }

    this.props.showPopup({
      type: PopupType.ErrorPopup,
      traceID: error?.headers?.["x-trace-identifier"],
      data: {
        ...texts,
        hidePopup: this.props.hidePopup,
      },
    });
  };

  private splitSuggestions = (suggestions: ISuggestion[], roleId: string) => {
    const clientsList: ISuggestion[] = [];
    const agencyList: ISuggestion[] = [];

    suggestions.forEach((item) => {
      const id = item.agency.id;

      if (id === DEFAULT_CLIENT_AGENCY_ID || id === NO_CLIENT_AGENCY_ID) {
        clientsList.push(item);
      } else {
        agencyList.push(item);
      }
    });

    const nextState = { clientsList, agencyList, removedList: [] };

    this.setState(nextState);
    this.props.onDataLoaded?.({ id: roleId, loadedData: nextState });
  };

  private switchToEditMode = (flag: boolean) => () => {
    this.props.switchEditMode(flag);
  };

  private handleDelete = async (performersIds: string[]): Promise<boolean> => {
    const { breakdown, role, deleteSuggestions } = this.props;

    try {
      await deleteSuggestions(breakdown.id, role.id, performersIds);

      const nextState = {
        removedList: [...this.state.removedList, ...performersIds],
      };

      this.setState(nextState);
      this.props.onDataLoaded?.({ id: role.id, loadedData: nextState });

      return true;
    } catch (e) {
      this.handleError(e);
    }

    return false;
  };
}

const mapStateToProps = (
  state: IApplicationState
): ISuggestionsViewAgentStoreProps => {
  const page = state.translation.pages[PageKey.Breakdown]!;

  return {
    texts: prepareTexts(page, state.translation.common),
    getSuggestions: (...args) => suggestionsService.getSuggestions(...args),
    deleteSuggestions: (...args) =>
      suggestionsService.deleteSuggestions(...args),
    productionTypes: state.facet[FacetStoreKey.ProductionType]!,
  };
};

const mapDispatchToProps: MapDispatchToProps<ISuggestionsViewAgentActions> = {
  showPopup,
  hidePopup,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  SuggestionsViewAgent
);
